import React, { useEffect, useState } from 'react';
import { useNavigate/*, useLocation */} from 'react-router-dom';
import api from 'config/axiosConfig';
import EntityActions from 'components/Entities/EntityActions';
import Report from 'components/Entities/Report';
import {
  Tooltip,
  Chip
} from '@mui/joy';
import { Ellipsis, Flag } from 'lucide-react';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import { useUser } from 'util/UserContext';
import { Impact, Influence } from 'util/constants';
import EntityAvatar from 'components/Entities/EntityAvatar';
import { 
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'ui/DropdownMenu';
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer';
import DialogConfirmation from 'ui/DialogConfirmation';
import { AxiosError } from 'axios';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from 'ui/Carousel';
import ImpactCard from 'components/Impacts/ImpactCard';

interface Props {
  influence: Influence;
  showMeta?: boolean;
  isInfluenceProfilePage?: boolean;
  isEntitySection?: boolean;
  showActions?: boolean;
  onVote?: () => void;
}

const InfluenceCard = ({
  influence,
  showMeta = true,
  isInfluenceProfilePage = false,
  isEntitySection = false,
}: Props) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const navigate = useNavigate();
  const influenceHref = `/influence/${influence.id}`;
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [impacts, setImpacts] = useState<Impact[]>([]);
  const [error, setError] = useState<string | null>(null);
  //const location = useLocation();

    const getInfluenceLabel = () => {
        if (!isEntitySection) return "Influence";

        //NOTE: Removing this for now, since we have EntityAvatar
        // Extract issue name from URL
        // const match = location.pathname.match(/\/issue\/([^/]+)\/influences/);
        // if (!match) return "Influence";

        //const issueNameFromUrl = decodeURIComponent(match[1]);
        //console.log('issueNameFromUrl', issueNameFromUrl);
        
        // Compare with influencer_name
        //return influence.influencer_name === issueNameFromUrl ? "Influencer" : "Influenced";
        return "Influence";
    };

    useEffect(() => {
        const fetchImpacts = async () => {
            try {
                const response = await api.get<Impact[]>(
                    `/api/impacts/influence/${influence.id}`
                )
                setImpacts(response.data)
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    setError('404: Failed to load impacts. Please try again later.')
                } else {
                    setError('Failed to load impacts. Please try again later.')
                }
            }
        }

        fetchImpacts();
    }, [influence.id])

  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const element = event.target as HTMLElement;
    if (element.tagName.toLowerCase() === 'a') return;
    if (element.tagName.toLowerCase() === 'button') return;
    if (!isInfluenceProfilePage) {
      event.stopPropagation();
      navigate(influenceHref);
    } else {
      event.preventDefault();
    }
  };

  const handleReportClick = () => {
    if (!currentUser) {
      setShowAuthModal(true);
    } else {
      setMenuOpen(false);
      setDrawerOpen(false);
      setIsReportModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/influences/id/${influence.id}`);
      setIsDeleteDialogOpen(false);
      if (isInfluenceProfilePage) {
        navigate('/');
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting influence:', error);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const MoreButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (
    <button
      type="button"
      ref={ref}
      {...props}
      className="card-more size-7 rounded-full flex justify-center items-center"
    >
      <Ellipsis className="icon-stroke size-5"/>
    </button>
  ))
  MoreButton.displayName = 'MoreButton'

  const MoreOptions = () => (
    <>
      {!userIsMobile ? (
        <div className="flex flex-col">
          <DropdownMenuItem onClick={handleReportClick}>
            <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
              <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
              <span>Report</span>
            </div>
          </DropdownMenuItem>
        </div>
      ) : (
        <>
          <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Flag className="size-4"/>
            <span>Report</span>
          </button>
        </>
      )}
    </>
  )

  return (
    <div className="card-container my-1">
      {isReportModalOpen && (
        <Report
          entityId={influence.id}
          entityType="influence"
          open={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
        />
      )}
      <article>
        <div className={`rounded-lg flex flex-col px-4 py-2 relative ${isInfluenceProfilePage ? 'card-main-profile-page' : 'card-main'}`}>
          <div className="entity-actions-container order-last">
            <EntityActions entity={influence} entityType="influence" />
          </div>
          <div className="card-content order-first">
            {showMeta && (
              <div className="card-meta text-sm flex items-center justify-between relative">
                <EntityAvatar 
                  entity={influence}
                  entityType="influence"
                  isEntityProfilePage={isInfluenceProfilePage}
                  isEntitySection={isEntitySection}
                />
                <div className="icon-more text-xl">
                  {userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                      <DrawerTrigger asChild>
                        <MoreButton />
                      </DrawerTrigger>
                      <DrawerContent title="Actions">
                        <MoreOptions />
                      </DrawerContent>
                    </Drawer>
                  ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                      <DropdownMenuTrigger asChild>
                        <MoreButton />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <MoreOptions />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}
            <div className="card-header mt-1" onClick={handleCardClick}>
              {isInfluenceProfilePage ? (
                <span className="card-title">
                  {influence.title.replace(/>/g, ' ❯ ')}
                </span>
              ) : (
                <a href={influenceHref} className="card-title">
                  {influence.title.replace(/>/g, ' ❯ ')}
                </a>
              )}
              <div className="card-tags flex flex-row gap-2 my-1 items-center">
                <Chip
                  component={'div' as any}
                  label={getInfluenceLabel()}
                  color="neutral"
                  variant="soft"
                  size="md"
                  sx={{
                    backgroundColor: '#DB638B',
                    color: '#fff',
                    fontSize: '0.75rem',
                  }}
                  >
                    {getInfluenceLabel()}
                </Chip>              
                <Tooltip title={formatDate(influence.created_at)} arrow>
                  <span className="card-date font-normal">
                    {formatRelativeTime(influence.created_at)}
                  </span>
                </Tooltip>
              </div>
            </div>
            {impacts.length > 0 && (
              <div className="impacts-carousel relative" onClick={handleCardClick}>
                <Carousel className="">
                  {error ? (
                    <div className="text-center text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {error}
                    </div>
                  ) : (
                    <>
                    <div className="flex justify-between mb-1">
                      <CarouselPrevious className="flex" size={'lg'}/>
                      <CarouselNext className="flex" size={'lg'}/>
                    </div>
                      <CarouselContent>
                        {impacts.map((impact) => (
                          <CarouselItem key={impact.id} className="">
                        <ImpactCard impact={impact} showMeta={false}/>
                      </CarouselItem>
                    ))}
                    {impacts.length > 1 && (
                      <CarouselItem>
                        <a 
                          href={`/influence/${influence.id}/impacts`}
                          className="h-full flex items-center justify-center p-2 rounded-lg hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                        >
                          <span className="font-semibold">View more impacts</span>
                        </a>
                      </CarouselItem>
                  )}
                      </CarouselContent>
                    </>
                  )}
                </Carousel>
              </div>
            )}
          </div>
        </div>
      </article>
      {isDeleteDialogOpen && (
        <DialogConfirmation 
          title="Delete Influence" 
          description="Are you sure you want to delete this influence?" 
          isOpen={isDeleteDialogOpen} 
          onConfirm={confirmDelete} 
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default InfluenceCard;